import React, { useState, useEffect } from "react";
import {
  supportAnalyticsService,
  AnalyticsMetrics,
  AnalyticsFilters,
} from "@core/services/SupportAnalyticsService";
import { AnalyticsCharts } from "../../components/admin/AnalyticsCharts";
import { PollModificationsKPI } from "@core/components/admin/PollModificationsKPI";
import { AiUsageAnalyticsKPI } from "@core/components/admin/AiUsageAnalyticsKPI";
import {
  PerformanceMetric,
  TrendItem,
} from "../../components/admin/PerformanceMetrics";
import { useAuth } from "@core/contexts/AuthContext";
import {
  BarChart3,
  Download,
  Calendar,
  Filter,
  TrendingUp,
  TrendingDown,
  ArrowLeft,
  Users,
  CheckCircle,
} from "lucide-react";

export const AnalyticsDashboard: React.FC = () => {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState<AnalyticsMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<AnalyticsFilters>({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // 30 jours
    endDate: new Date(),
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadMetrics();
  }, [filters]);

  const loadMetrics = async () => {
    try {
      setLoading(true);
      const data = await supportAnalyticsService.calculateMetrics(filters);
      setMetrics(data);
    } catch (error) {
      console.error("Error loading analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      // Importer jspdf et html2canvas dynamiquement
      const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
        import("jspdf"),
        import("html2canvas"),
      ]);

      // Capter le dashboard
      const element = document.getElementById("mission-control-dashboard");
      if (!element) {
        console.error("Dashboard element not found");
        return;
      }

      // Capturer en image haute qualité
      const canvas = await html2canvas(element, {
        backgroundColor: "#111827", // color-exempt: html2canvas export snapshot, must stay fixed
        scale: 2,
        logging: false,
        useCORS: true,
        allowTaint: true,
      });

      const imgData = canvas.toDataURL("image/png");

      // Créer le PDF
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      // Ajouter l'image au PDF
      const imgWidth = 280; // Largeur en mm (A4 landscape)
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, "PNG", 10, 10, imgWidth, imgHeight);

      // Ajouter le titre et la date
      pdf.setFontSize(16);
      pdf.setTextColor(0, 0, 0);
      pdf.text("Mission Control Dashboard", 10, imgHeight + 20);

      pdf.setFontSize(10);
      pdf.text(
        `Date: ${new Date().toLocaleDateString("fr-FR")}`,
        10,
        imgHeight + 30,
      );

      // Sauvegarder le PDF
      pdf.save(`mission-control-${new Date().toISOString().split("T")[0]}.pdf`);
    } catch (error) {
      console.error("Error exporting PDF:", error);
      // Fallback : exporter en PNG si jspdf échoue
      const html2canvas = (await import("html2canvas")).default;
      const element = document.getElementById("mission-control-dashboard");
      if (element) {
        const canvas = await html2canvas(element, {
          backgroundColor: "#111827", // color-exempt: html2canvas export snapshot, must stay fixed
          scale: 2,
          logging: false,
        });
        const imgData = canvas.toDataURL("image/png");
        const link = document.createElement("a");
        link.download = `mission-control-${new Date().toISOString().split("T")[0]}.png`;
        link.href = imgData;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  };

  const handleExportCSV = async () => {
    try {
      const csvContent = await supportAnalyticsService.exportToCSV(filters);

      // Créer et télécharger le fichier
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `support-analytics-${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting CSV:", error);
    }
  };

  const handleFilterChange = (newFilters: Partial<AnalyticsFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const formatDate = (date: Date) => {
    return date.toISOString().split("T")[0];
  };

  if (!user) {
    return (
      <div className="min-h-screen-safe bg-background flex items-center justify-center">
        <div className="text-foreground">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen-safe bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <BarChart3 className="w-6 h-6 text-primary" />
              <h1 className="text-xl font-semibold text-foreground">
                Analytics Support
              </h1>
            </div>

            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-3 py-2 bg-muted text-foreground rounded-lg hover:bg-accent transition-colors"
              >
                <Filter className="w-4 h-4" />
                Filtres
              </button>

              <button
                onClick={handleExportCSV}
                disabled={loading || !metrics}
                className="flex items-center gap-2 px-3 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>

              <button
                onClick={handleExportPDF}
                disabled={loading || !metrics}
                className="flex items-center gap-2 px-3 py-2 bg-destructive text-destructive-foreground rounded-lg hover:bg-destructive/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Download className="w-4 h-4" />
                Export PDF
              </button>

              <button
                onClick={() => (window.location.href = "/admin")}
                className="flex items-center gap-2 px-3 py-2 bg-muted text-foreground rounded-lg hover:bg-accent transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Retour Admin
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filtres */}
      {showFilters && (
        <div className="bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">
                  Date de début
                </label>
                <input
                  type="date"
                  value={formatDate(filters.startDate)}
                  onChange={(e) =>
                    handleFilterChange({ startDate: new Date(e.target.value) })
                  }
                  className="w-full px-3 py-2 bg-card border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">
                  Date de fin
                </label>
                <input
                  type="date"
                  value={formatDate(filters.endDate)}
                  onChange={(e) =>
                    handleFilterChange({ endDate: new Date(e.target.value) })
                  }
                  className="w-full px-3 py-2 bg-card border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">
                  Statut
                </label>
                <select
                  value={filters.status || ""}
                  onChange={(e) =>
                    handleFilterChange({ status: e.target.value || undefined })
                  }
                  className="w-full px-3 py-2 bg-card border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Tous</option>
                  <option value="open">Ouverts</option>
                  <option value="in_progress">En cours</option>
                  <option value="resolved">Résolus</option>
                  <option value="closed">Fermés</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contenu principal */}
      <div
        id="mission-control-dashboard"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
      >
        {/* KPIs principaux */}
        {metrics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <KPICard
              title="Tickets Total"
              value={metrics.totalTickets}
              icon={<Users className="w-5 h-5" />}
              color="blue"
              subtitle="30 derniers jours"
            />
            <KPICard
              title="Taux SLA"
              value={`${metrics.slaComplianceRate.toFixed(1)}%`}
              icon={<CheckCircle className="w-5 h-5" />}
              color="green"
              subtitle={
                metrics.totalTickets > 0 ? "Objectif: 95%" : "Pas de données"
              }
            />
            <KPICard
              title="Satisfaction"
              value={metrics.satisfactionScore.toFixed(1)}
              icon={<CheckCircle className="w-5 h-5" />}
              color="purple"
              subtitle="Sur 5"
            />
            <KPICard
              title="Tickets en Retard"
              value={metrics.overdueTickets}
              icon={<Users className="w-5 h-5" />}
              color="red"
              subtitle={
                metrics.totalTickets > 0 ? "Nécessite action" : "Pas de données"
              }
            />
          </div>
        )}

        {/* KPI Modifications IA (cf. spec
            Docs/SPECS/2026-05-12-spec-modifications-proposition-sondage.md) */}
        <div className="mb-8">
          <PollModificationsKPI />
        </div>

        {/* KPI usage réel chat IA vs manuel, croisé par plan utilisateur */}
        <div className="mb-8">
          <AiUsageAnalyticsKPI />
        </div>

        {/* Graphiques */}
        <AnalyticsCharts metrics={metrics} loading={loading} />

        {/* Objectifs et performances */}
        {metrics && (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">
                Objectifs de Performance
              </h3>
              <div className="space-y-4">
                <PerformanceMetric
                  label="Taux de déflexion"
                  current={
                    metrics.totalTickets > 0 ? metrics.deflectionRate : 0
                  }
                  target={70}
                  unit="%"
                />
                <PerformanceMetric
                  label="Auto-résolution"
                  current={
                    metrics.totalTickets > 0 ? metrics.autoResolutionRate : 0
                  }
                  target={30}
                  unit="%"
                />
                <PerformanceMetric
                  label="Temps de réponse"
                  current={
                    metrics.totalTickets > 0 ? metrics.averageResponseTime : 0
                  }
                  target={2}
                  unit="h"
                />
              </div>
            </div>

            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">
                Tendances
              </h3>
              <div className="space-y-3">
                <TrendItem
                  label="Tickets résolus"
                  value={metrics.resolvedTickets}
                  trend={metrics.totalTickets > 0 ? "stable" : "nodata"}
                />
                <TrendItem
                  label="Tickets ouverts"
                  value={metrics.openTickets}
                  trend={metrics.totalTickets > 0 ? "down" : "nodata"}
                />
                <TrendItem
                  label="Satisfaction"
                  value={metrics.satisfactionScore}
                  trend={metrics.totalTickets > 0 ? "up" : "nodata"}
                  format={(value) => `${value}/5`}
                />
                <TrendItem
                  label="Ticket Rate"
                  value={metrics.ticketRate}
                  trend={
                    metrics.totalTickets > 0
                      ? metrics.ticketRate < 20
                        ? "good"
                        : "bad"
                      : "nodata"
                  }
                  format={(value) => `${value}/1000`}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

interface KPICardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: "blue" | "green" | "purple" | "red";
  subtitle: string;
}

const KPICard: React.FC<KPICardProps> = ({
  title,
  value,
  icon,
  color,
  subtitle,
}) => {
  const colorClasses = {
    blue: "bg-info/10 border-info/20 text-info",
    green: "bg-success/10 border-success/20 text-success",
    purple: "bg-primary/10 border-primary/20 text-primary",
    red: "bg-destructive/10 border-destructive/20 text-destructive",
  };

  return (
    <div
      className={`bg-card border border-border rounded-xl p-6 ${colorClasses[color]}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="p-2 rounded-lg bg-muted">{icon}</div>
      </div>
      <h3 className="text-sm font-medium text-muted-foreground mb-1">
        {title}
      </h3>
      <p className="text-2xl font-bold text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
    </div>
  );
};
